/** Shared page builder / runtime widget types and helpers (mirrors hub page.v1). */

export type PageGrid = { x: number; y: number; w: number; h: number };

export type PageWidgetSource = { type: "operation_node"; operation_id: string; node_id: string; port_id?: string };

export type PageContentWidget = {
  id: string;
  kind?: "content";
  source: PageWidgetSource;
  title_override?: string;
  grid: PageGrid;
};

export type PageTabPanel = { id: string; label: string; widgets: PageWidget[] };

export type PageTabsWidget = {
  id: string;
  kind: "tabs";
  title_override?: string;
  grid: PageGrid;
  tabs: PageTabPanel[];
};

export type PageWidget = PageContentWidget | PageTabsWidget;

export function isTabsWidget(w: PageWidget): w is PageTabsWidget {
  return "kind" in w && w.kind === "tabs";
}

export function flattenContentWidgets(widgets: PageWidget[]): PageContentWidget[] {
  const out: PageContentWidget[] = [];
  function walk(panel: PageWidget[]) {
    for (const w of panel) {
      if (isTabsWidget(w)) {
        for (const t of w.tabs) walk(t.widgets);
      } else {
        out.push(w);
      }
    }
  }
  for (const w of widgets) {
    if (isTabsWidget(w)) {
      for (const t of w.tabs) walk(t.widgets);
    } else {
      out.push(w);
    }
  }
  return out;
}

export function findContentWidgetById(widgets: PageWidget[], id: string): PageContentWidget | undefined {
  for (const w of widgets) {
    if (!isTabsWidget(w) && w.id === id) return w;
    if (isTabsWidget(w)) {
      for (const t of w.tabs) {
        const found = findContentInPanel(t.widgets, id);
        if (found) return found;
      }
    }
  }
  return undefined;
}

function findContentInPanel(panel: PageWidget[], id: string): PageContentWidget | undefined {
  for (const child of panel) {
    if (!isTabsWidget(child) && child.id === id) return child;
    if (isTabsWidget(child)) {
      for (const t of child.tabs) {
        const found = findContentInPanel(t.widgets, id);
        if (found) return found;
      }
    }
  }
  return undefined;
}

/** Any tab container in the tree (root or nested inside a tab panel). */
export function findTabsWidgetById(widgets: PageWidget[], id: string): PageTabsWidget | undefined {
  for (const w of widgets) {
    if (isTabsWidget(w)) {
      if (w.id === id) return w;
      for (const t of w.tabs) {
        const found = findTabsWidgetById(t.widgets, id);
        if (found) return found;
      }
    }
  }
  return undefined;
}

export function widgetSourceKey(source: PageWidgetSource): string {
  const port = source.port_id?.trim() || "tabular_out";
  return `op_node:${source.operation_id}:${source.node_id}:${port}`;
}

export function vizNodeLabelKey(source: PageWidgetSource): string {
  return `op_node:${source.operation_id}:${source.node_id}`;
}

export function sourcesFromWidgetsUnique(widgets: PageWidget[]): PageWidgetSource[] {
  return Array.from(
    new Map(flattenContentWidgets(widgets).map((w) => [widgetSourceKey(w.source), w.source] as const)).values()
  );
}

function mapPanelUpdateContent(
  panel: PageWidget[],
  id: string,
  updater: (w: PageContentWidget) => PageContentWidget
): PageWidget[] {
  return panel.map((child) => {
    if (!isTabsWidget(child)) {
      return child.id === id ? updater(child) : child;
    }
    return {
      ...child,
      tabs: child.tabs.map((t) => ({
        ...t,
        widgets: mapPanelUpdateContent(t.widgets, id, updater)
      }))
    };
  });
}

export function updateContentWidgetById(
  widgets: PageWidget[],
  id: string,
  updater: (w: PageContentWidget) => PageContentWidget
): PageWidget[] {
  return widgets.map((w) => {
    if (isTabsWidget(w)) {
      return {
        ...w,
        tabs: w.tabs.map((t) => ({
          ...t,
          widgets: mapPanelUpdateContent(t.widgets, id, updater)
        }))
      };
    }
    return w.id === id ? updater(w) : w;
  });
}

function updateTabsNode(w: PageWidget, id: string, updater: (tw: PageTabsWidget) => PageTabsWidget): PageWidget {
  if (!isTabsWidget(w)) return w;
  if (w.id === id) return updater(w);
  return {
    ...w,
    tabs: w.tabs.map((t) => ({
      ...t,
      widgets: t.widgets.map((c) => updateTabsNode(c, id, updater))
    }))
  };
}

export function updateTabsWidgetById(widgets: PageWidget[], id: string, updater: (w: PageTabsWidget) => PageTabsWidget): PageWidget[] {
  return widgets.map((w) => updateTabsNode(w, id, updater));
}

export function removeWidgetFromPage(widgets: PageWidget[], widgetId: string): PageWidget[] {
  return widgets
    .filter((w) => w.id !== widgetId)
    .map((w) => {
      if (!isTabsWidget(w)) return w;
      return {
        ...w,
        tabs: w.tabs.map((t) => ({
          ...t,
          widgets: removeWidgetFromPage(t.widgets, widgetId)
        }))
      };
    });
}

/** All widget ids in a tab panel subtree (content + nested tab containers). */
export function collectWidgetIdsInPanelTree(panel: PageWidget[]): Set<string> {
  const ids = new Set<string>();
  function walk(items: PageWidget[]) {
    for (const w of items) {
      ids.add(w.id);
      if (isTabsWidget(w)) {
        for (const t of w.tabs) walk(t.widgets);
      }
    }
  }
  walk(panel);
  return ids;
}

/** Removes one tab panel from a tabs widget; returns null if that would leave zero tabs or tab is missing. */
export function removeTabPanelFromTabsWidget(
  widgets: PageWidget[],
  containerId: string,
  tabId: string
): PageWidget[] | null {
  const tw = findTabsWidgetById(widgets, containerId);
  if (!tw || tw.tabs.length <= 1) return null;
  if (!tw.tabs.some((t) => t.id === tabId)) return null;
  return updateTabsWidgetById(widgets, containerId, (w) => ({
    ...w,
    tabs: w.tabs.filter((t) => t.id !== tabId)
  }));
}

function mkTabId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createDefaultTabsWidget(args: { id: string; grid: PageGrid }): PageTabsWidget {
  return {
    id: args.id,
    kind: "tabs",
    grid: args.grid,
    tabs: [
      { id: mkTabId(), label: "Tab 1", widgets: [] },
      { id: mkTabId(), label: "Tab 2", widgets: [] }
    ]
  };
}

function findContentInPanelForParent(
  containerId: string,
  tabId: string,
  panel: PageWidget[],
  contentId: string
): { containerId: string; tabId: string; panel: PageWidget[] } | null {
  for (const child of panel) {
    if (!isTabsWidget(child) && child.id === contentId) {
      return { containerId, tabId, panel };
    }
  }
  for (const child of panel) {
    if (isTabsWidget(child)) {
      for (const t of child.tabs) {
        const hit = findContentInPanelForParent(child.id, t.id, t.widgets, contentId);
        if (hit) return hit;
      }
    }
  }
  return null;
}

export function findTabParentForContent(
  widgets: PageWidget[],
  contentId: string
): { containerId: string; tabId: string; panel: PageWidget[] } | null {
  for (const w of widgets) {
    if (!isTabsWidget(w)) continue;
    for (const t of w.tabs) {
      const hit = findContentInPanelForParent(w.id, t.id, t.widgets, contentId);
      if (hit) return hit;
    }
  }
  return null;
}

export function appendWidgetToTab(
  widgets: PageWidget[],
  containerId: string,
  tabId: string,
  item: PageWidget
): PageWidget[] {
  for (let i = 0; i < widgets.length; i++) {
    const w = widgets[i];
    if (isTabsWidget(w) && w.id === containerId) {
      const clone = [...widgets];
      clone[i] = {
        ...w,
        tabs: w.tabs.map((t) => (t.id === tabId ? { ...t, widgets: [...t.widgets, item] } : t))
      };
      return clone;
    }
  }
  return widgets.map((w) => {
    if (!isTabsWidget(w)) return w;
    return {
      ...w,
      tabs: w.tabs.map((t) => ({
        ...t,
        widgets: appendWidgetToTab(t.widgets, containerId, tabId, item)
      }))
    };
  });
}

export function appendContentWidgetToTab(
  widgets: PageWidget[],
  containerId: string,
  tabId: string,
  cw: PageContentWidget
): PageWidget[] {
  return appendWidgetToTab(widgets, containerId, tabId, cw);
}

/** Widgets sharing the same 12-col surface (page root or one tab panel), for overlap checks while dragging. */
export type PageGridOccupant = { id: string; grid: PageGrid };

export function findWidgetGridById(widgets: PageWidget[], widgetId: string): PageGrid | null {
  for (const w of widgets) {
    if (w.id === widgetId) return w.grid;
    if (isTabsWidget(w)) {
      for (const t of w.tabs) {
        const g = findWidgetGridById(t.widgets, widgetId);
        if (g) return g;
      }
    }
  }
  return null;
}

export function findSiblingOccupantsForWidget(widgets: PageWidget[], widgetId: string): PageGridOccupant[] | null {
  for (const w of widgets) {
    if (!isTabsWidget(w)) {
      if (w.id === widgetId) {
        return widgets.map((x) => ({ id: x.id, grid: x.grid }));
      }
      continue;
    }
    if (w.id === widgetId) {
      return widgets.map((x) => ({ id: x.id, grid: x.grid }));
    }
    for (const t of w.tabs) {
      const shallowHit = t.widgets.find((c) => c.id === widgetId);
      if (shallowHit) {
        return t.widgets.map((c) => ({ id: c.id, grid: c.grid }));
      }
      const nested = findSiblingOccupantsForWidget(t.widgets, widgetId);
      if (nested) return nested;
    }
  }
  return null;
}

function updateWidgetGridOne(w: PageWidget, widgetId: string, grid: PageGrid): PageWidget {
  if (w.id === widgetId) return { ...w, grid };
  if (!isTabsWidget(w)) return w;
  return {
    ...w,
    tabs: w.tabs.map((t) => ({
      ...t,
      widgets: t.widgets.map((c) => updateWidgetGridOne(c, widgetId, grid))
    }))
  };
}

export function updateWidgetGridById(widgets: PageWidget[], widgetId: string, grid: PageGrid): PageWidget[] {
  return widgets.map((w) => updateWidgetGridOne(w, widgetId, grid));
}

/**
 * The port a widget's source reads, as the hub resolved it. A source may omit
 * `port_id`; batch-resolve then resolves it per node type (a tantable, KPI
 * card, or reactive viz reads `render_out`) and reports the resolved port on
 * the item's `source`. Declared port wins; the hub's answer comes next; the
 * hub's own default last.
 */
export function resolvedSourcePort(
  source: PageWidgetSource,
  item: { source?: PageWidgetSource } | undefined
): string {
  return source.port_id?.trim() || item?.source?.port_id?.trim() || "tabular_out";
}

export type BatchResolveError = { source_key: string; code: string; message: string };

/**
 * Index a batch-resolve response by the keys THIS client computes for the
 * sources it asked about (`widgetSourceKey`), not only by the keys the hub
 * returns. The two disagree whenever a source omits `port_id`: the client
 * defaults to `tabular_out`, the hub resolves the port per node type and keys
 * the item on that. Without this step a port-less render widget never finds
 * its row and shows as never run.
 *
 * Every item and error is stored under the hub's key AND under the key of
 * each requested port-less source on the same operation/node.
 */
export function indexBatchResolve<I extends { source_key: string }>(
  requested: PageWidgetSource[],
  data: { items: I[]; errors: BatchResolveError[] }
): { items: Record<string, I>; errors: Record<string, BatchResolveError> } {
  const portlessKeysByNode = new Map<string, string[]>();
  for (const s of requested) {
    if (s.port_id?.trim()) continue;
    const nodeKey = vizNodeLabelKey(s);
    const list = portlessKeysByNode.get(nodeKey) ?? [];
    list.push(widgetSourceKey(s));
    portlessKeysByNode.set(nodeKey, list);
  }
  const aliasesFor = (sourceKey: string): string[] => {
    // op_node:<operation_id>:<node_id>:<port>
    const parts = sourceKey.split(":");
    if (parts.length < 4) return [sourceKey];
    const nodeKey = parts.slice(0, 3).join(":");
    return [sourceKey, ...(portlessKeysByNode.get(nodeKey) ?? [])];
  };

  const items: Record<string, I> = {};
  for (const item of data.items) {
    for (const k of aliasesFor(item.source_key)) items[k] = item;
  }
  const errors: Record<string, BatchResolveError> = {};
  for (const err of data.errors) {
    for (const k of aliasesFor(err.source_key)) {
      // An item under the same key wins over an error: a port-less node that
      // resolved on one port is not "missing" because another port had none.
      if (items[k]) continue;
      errors[k] = err;
    }
  }
  return { items, errors };
}

/**
 * A widget whose step has no output yet but is being computed in the
 * background (the hub's page heal): no render model, `needsRecompute` set.
 * The page shows "preparing" and re-resolves until the row lands.
 */
export function isPreparingItem(
  item: { last?: { renderModel?: unknown }; meta?: { needsRecompute?: boolean } } | undefined
): boolean {
  return !!item && !item.last?.renderModel && item.meta?.needsRecompute === true;
}

/** Re-resolve cadence and cap while any widget is preparing (3 s × 60 = 3 min). */
export const PREPARING_POLL_MS = 3000;
export const PREPARING_POLL_MAX = 60;
